import Vue from 'vue';
import * as Sentry from '@sentry/vue';

/**
 * 前端錯誤蒐集。
 *
 * 只收錯誤，不開 Performance tracing 與 Session Replay，兩者都會另外吃 Sentry 額度。
 *
 * ⚠️ 不要傳 integrations。v7 只要傳了這個欄位就會整組覆蓋掉預設的
 * Breadcrumbs / GlobalHandlers / LinkedErrors，反而變成幾乎什麼都收不到。
 * 維持不傳，預設值本來就不含 BrowserTracing 與 Replay。
 *
 * DSN 只寫在 .env.stg 與 .env.production；本機 serve 讀不到就整個跳過 init，
 * 開發時的錯誤不會污染線上專案。
 */
export default function initSentry() {
    const dsn = process.env.VUE_APP_SENTRY_DSN;
    if (!dsn) {
        return;
    }

    Sentry.init({
        Vue,
        dsn,
        environment: process.env.VUE_APP_SENTRY_ENV || process.env.NODE_ENV,
        // 由 Makefile 帶入 git short sha，要和 sourcemap 上傳時的 release 對得起來
        release: process.env.VUE_APP_RELEASE || undefined,
        // 出錯元件的 props 一起帶上來，比較好重現
        attachProps: true,
        // 錯誤照樣印到 console，不改變原本的開發手感
        logErrors: true,
        ignoreErrors: [
            // 瀏覽器的已知噪音，不是真的錯誤
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications',
            // vue-router 3.1+ 的 navigation failure：跳轉被新跳轉取代或重複跳轉同頁，
            // router 本來就會略過，使用者無感，只是沒 catch 的 push() 變成 unhandled rejection
            'Navigation cancelled from',
            'NavigationDuplicated',
            'Avoided redundant navigation to current location',
            // FB / IG 等 App 內建瀏覽器自己注入的 script 出錯
            'Java object is gone',
            // Outlook SafeLinks 等郵件掃描爬蟲造成
            'Object Not Found Matching Id',
        ],
        denyUrls: [
            // 使用者自己裝的擴充功能丟出來的錯誤與本站無關
            /^chrome-extension:\/\//i,
            /^moz-extension:\/\//i,
            /^safari-extension:\/\//i,
        ],
        beforeSend(event, hint) {
            // 401 = token 過期，src/api/index.js 的 interceptor 已經會清 token 登出，屬正常流程
            const error = hint && hint.originalException;
            if (error && error.response && error.response.status === 401) {
                return null;
            }
            return event;
        },
    });
}
